"use client";

import { useId } from "react";

/**
 * 品牌标识「Oblique Play」。
 *
 * 字母整体剪切 -11°，圆盘跟着变成椭圆；但盘里的播放三角形在字形内部
 * 预先反向剪切 +11°，被词本身的剪切正好抵消，左边缘保持绝对垂直——
 * 播放键是人人认得的形状，斜掉会读成「画错了」而不是「快」。
 *
 * 三角形顶点已按 tan(11°)=0.19438 绕圆心 (32,32) 预算好写进 path，
 * 所以运行时不需要任何 transform。改倾斜角要同时改这里的顶点和
 * globals.css 里 .logo-wordmark 的 skewX。
 */

const DISC = "M32 4a28 28 0 1 0 0 56 28 28 0 1 0 0-56Z";

/** 词内用：预剪切过，被 -11° 抵消后左边垂直 */
const TRIANGLE_IN_WORD = "M23.47 19 46 32 28.53 45Z";

/** 独立图标用：出了词没有倾斜可继承，正圆 + 正三角 */
const TRIANGLE_UPRIGHT = "M26 19 46 32 26 45Z";

function Disc({
  triangle,
  className,
}: {
  triangle: string;
  className?: string;
}) {
  // 同一页可能渲染多个 logo（导航 + 页脚），渐变 id 必须每个实例唯一
  const gradientId = useId();
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden="true">
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="var(--accent)" />
          <stop offset="1" stopColor="var(--accent-2)" />
        </linearGradient>
      </defs>
      {/* evenodd：三角形是真镂空，放到任何底色上都不用换版本 */}
      <path
        fill={`url(#${gradientId})`}
        fillRule="evenodd"
        d={`${DISC}${triangle}`}
      />
    </svg>
  );
}

/** 完整字标。size 是像素字号——整个标识按 em 缩放。 */
export function Wordmark({ size = 19 }: { size?: number }) {
  return (
    <span className="logo-wordmark" style={{ fontSize: size }}>
      Easy
      <span className="logo-shot">
        Sh
        <Disc triangle={TRIANGLE_IN_WORD} className="logo-disc" />t
      </span>
    </span>
  );
}

/** 独立圆盘图标：favicon、App 图标、头像位、视频角标 */
export function LogoIcon({ className }: { className?: string }) {
  return <Disc triangle={TRIANGLE_UPRIGHT} className={className} />;
}
